// ---------------------------------------------------------------------------
// Conversation HTML Renderer
// Uses DOMParser to transform custom conversation tags (<m>, <chat>,
// <utterance>) into clean, safe HTML with CSS classes.
// ---------------------------------------------------------------------------

/** Date header pattern: *** Wednesday, January 7, 2026 *** */
const DATE_HEADER_RE = /^\*{3}\s*(.+?)\s*\*{3}$/;

/** Extracts just the "Month Day, Year" part from a date header string */
const MONTH_DATE_RE =
  /(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d+,?\s*\d{4}/;

/**
 * Escape a string for safe inclusion in HTML text content.
 * DOMParser already handles entities when reading textContent, but we need
 * this when building output HTML from extracted strings.
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Extract safe inner HTML from a DOM element, preserving only basic
 * formatting tags. All text content is escaped via the DOM's own
 * textContent mechanism.
 */
function safeInnerHtml(el: Element): string {
  const allowedInline = new Set([
    'p',
    'br',
    'strong',
    'em',
    'b',
    'i',
    'ul',
    'ol',
    'li',
    'blockquote',
    'a',
    'span',
    'div',
    'table',
    'tr',
    'td',
    'th',
    'thead',
    'tbody',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
  ]);

  function walk(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return escapeHtml(node.textContent ?? '');
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const elem = node as Element;
    const tag = elem.tagName.toLowerCase();

    // Skip dangerous tags entirely
    if (tag === 'script' || tag === 'style' || tag === 'iframe') {
      return '';
    }

    // Recurse into children
    let inner = '';
    for (const child of Array.from(elem.childNodes)) {
      inner += walk(child);
    }

    // If tag is allowed, wrap content; otherwise just return inner text
    if (allowedInline.has(tag)) {
      if (tag === 'br') {
        return '<br />';
      }
      if (tag === 'a') {
        const href = elem.getAttribute('href') ?? '';
        const safeHref = /^javascript\s*:/i.test(href) ? '#' : escapeHtml(href);
        return `<a href="${safeHref}" rel="noopener noreferrer" target="_blank">${inner}</a>`;
      }
      return `<${tag}>${inner}</${tag}>`;
    }

    return inner;
  }

  let result = '';
  for (const child of Array.from(el.childNodes)) {
    result += walk(child);
  }
  return result;
}

/**
 * Format a date+time string in Teams-style relative format:
 * - Today: "3:37 PM"
 * - Yesterday: "Yesterday 3:37 PM"
 * - Within 7 days: "Wednesday 3:37 PM"
 * - Older: "1/7 10:44 AM"
 */
function formatTeamsTime(dateStr: string | null, timeStr: string): string {
  // Normalize: strip time-of-day qualifiers and "at" keyword
  // e.g. "January 7, 2026 at 10:44 AM, Morning" → "January 7, 2026 10:44 AM"
  const normalized = timeStr
    .replace(/,?\s*(?:Morning|Afternoon|Evening|Night)/gi, '')
    .replace(/\bat\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  // If timeStr already contains a full date (has a month name), parse it directly
  const hasFull = MONTH_DATE_RE.test(normalized);
  const msgDate = hasFull
    ? new Date(normalized)
    : dateStr
      ? new Date(`${dateStr} ${normalized}`)
      : null;

  if (!msgDate || isNaN(msgDate.getTime())) {
    // Fallback: strip seconds and return raw
    return normalized.replace(/(\d+:\d+):\d+(\s*[AaPp][Mm])/g, '$1$2').trim();
  }

  // Format the time portion from the parsed date object
  const h = msgDate.getHours() % 12 || 12;
  const min = msgDate.getMinutes().toString().padStart(2, '0');
  const ampm = msgDate.getHours() >= 12 ? 'PM' : 'AM';
  const shortTime = `${h.toString()}:${min} ${ampm}`;

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgStart = new Date(
    msgDate.getFullYear(),
    msgDate.getMonth(),
    msgDate.getDate(),
  );
  const diffDays = Math.round(
    (todayStart.getTime() - msgStart.getTime()) / 86400000,
  );

  if (diffDays === 0) return shortTime;
  if (diffDays === 1) return `Yesterday ${shortTime}`;
  if (diffDays < 7) {
    return `${msgDate.toLocaleDateString('en-US', { weekday: 'long' })} ${shortTime}`;
  }
  const m = (msgDate.getMonth() + 1).toString();
  const d = msgDate.getDate().toString();
  if (msgDate.getFullYear() !== now.getFullYear()) {
    return `${m}/${d}/${msgDate.getFullYear().toString()} ${shortTime}`;
  }
  return `${m}/${d} ${shortTime}`;
}

/**
 * Check text nodes at the top level of the parsed body for date header
 * lines (*** date ***). Returns just the short date (e.g. "January 7, 2026")
 * stripped of day-of-week and time-of-day qualifiers, or null if not a header.
 */
function extractDateFromHeader(text: string): string | null {
  const trimmed = text.trim();
  const match = DATE_HEADER_RE.exec(trimmed);
  if (!match) return null;
  const full = match[1] ?? '';
  // Try to extract just "Month Day, Year"
  const monthMatch = MONTH_DATE_RE.exec(full);
  return monthMatch ? monthMatch[0] : full;
}

/**
 * Render conversation history HTML containing custom <m> or <chat> tags
 * into clean, safe HTML with CSS classes.
 *
 * CSS classes used (define in component makeStyles):
 * - conv-date-header: date separator line
 * - conv-msg-card-pa: PA message card (blue left border)
 * - conv-msg-card-ma: MA message card (purple left border)
 * - conv-msg-card-generic: fallback message card
 * - conv-msg-header: sender + timestamp row
 * - conv-msg-sender: bold sender name
 * - conv-msg-time: gray timestamp
 * - conv-msg-body: message content area
 */
export function renderConversationHistory(raw: string): string {
  if (!raw) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(raw, 'text/html');
  const body = doc.body;
  const parts: string[] = [];
  let currentDate: string | null = null;

  for (const node of Array.from(body.childNodes)) {
    // Text nodes — check for date headers or plain text
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (!text.trim()) continue;

      // Check each line for date headers
      const lines = text.split('\n');
      for (const line of lines) {
        if (!line.trim()) continue;
        const date = extractDateFromHeader(line);
        if (date) {
          currentDate = date;
        } else {
          parts.push(`<p>${escapeHtml(line.trim())}</p>`);
        }
      }
      continue;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) continue;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    // PA format: <m s="sender" time="timestamp">
    if (tag === 'm') {
      const sender = el.getAttribute('s') ?? el.getAttribute('id') ?? '';
      const time = el.getAttribute('time') ?? '';
      const content = safeInnerHtml(el);

      const displayTime = time ? formatTeamsTime(currentDate, time) : '';
      const timeHtml = displayTime
        ? ` <span class="conv-msg-time">${escapeHtml(displayTime)}</span>`
        : '';

      parts.push(
        `<div class="conv-msg-card-pa">` +
          `<div class="conv-msg-header">` +
          `<span class="conv-msg-sender">${escapeHtml(sender)}</span>` +
          `${timeHtml}` +
          `</div>` +
          `<div class="conv-msg-body">${content}</div>` +
          `</div>`,
      );
      continue;
    }

    // MA format: <chat user="sender" datetime="timestamp">
    if (tag === 'chat') {
      const user = el.getAttribute('user') ?? '';
      const datetime = el.getAttribute('datetime') ?? '';
      const content = safeInnerHtml(el);

      const displayTime = datetime
        ? formatTeamsTime(currentDate, datetime)
        : '';
      const dtHtml = displayTime
        ? ` <span class="conv-msg-time">${escapeHtml(displayTime)}</span>`
        : '';

      parts.push(
        `<div class="conv-msg-card-ma">` +
          `<div class="conv-msg-header">` +
          `<span class="conv-msg-sender">${escapeHtml(user)}</span>` +
          `${dtHtml}` +
          `</div>` +
          `<div class="conv-msg-body">${content}</div>` +
          `</div>`,
      );
      continue;
    }

    // Standard HTML elements — pass through with sanitization
    if (el.nodeType === Node.ELEMENT_NODE) {
      // Check for date headers in top-level text-like elements
      const textOnly = (el.textContent ?? '').trim();
      const date = extractDateFromHeader(textOnly);
      if (date && !el.querySelector('*')) {
        currentDate = date;
        continue;
      }

      // Sanitize and include standard elements
      const inner = safeInnerHtml(el);
      if (inner.trim()) {
        const safeTag = [
          'p',
          'div',
          'ul',
          'ol',
          'blockquote',
          'table',
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
        ].includes(tag)
          ? tag
          : 'div';
        parts.push(`<${safeTag}>${inner}</${safeTag}>`);
      }
    }
  }

  // If we got no output but had input, treat as plain text
  if (parts.length === 0 && raw.trim()) {
    return `<p>${escapeHtml(raw)}</p>`;
  }

  return parts.join('\n');
}

/**
 * Render transcript HTML containing custom <utterance> tags into clean,
 * safe HTML with CSS classes.
 *
 * CSS classes used (define in component makeStyles):
 * - transcript-row: single utterance row
 * - transcript-speaker: bold speaker name
 * - transcript-time: gray timestamp
 * - transcript-text: spoken text content
 */
export function renderTranscript(raw: string): string {
  if (!raw) return '';

  // Check if the content has utterance tags at all
  if (!/<utterance[\s>]/i.test(raw)) {
    // Plain text transcript — return null-signal so component renders as-is
    return '';
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(raw, 'text/html');
  const body = doc.body;
  const parts: string[] = [];

  for (const node of Array.from(body.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent ?? '').trim();
      if (text) {
        parts.push(`<div class="transcript-row">${escapeHtml(text)}</div>`);
      }
      continue;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) continue;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    if (tag === 'utterance') {
      const speaker =
        el.getAttribute('speaker') ?? el.getAttribute('from') ?? '';
      const timestamp =
        el.getAttribute('timestamp') ?? el.getAttribute('datetime') ?? '';
      const text = el.textContent ?? '';

      const timeHtml = timestamp
        ? `<span class="transcript-time">${escapeHtml(timestamp)}</span> `
        : '';

      parts.push(
        `<div class="transcript-row">` +
          `${timeHtml}` +
          `<span class="transcript-speaker">${escapeHtml(speaker)}</span>: ` +
          `<span class="transcript-text">${escapeHtml(text.trim())}</span>` +
          `</div>`,
      );
      continue;
    }

    // Other elements — just extract text
    const text = (el.textContent ?? '').trim();
    if (text) {
      parts.push(`<div class="transcript-row">${escapeHtml(text)}</div>`);
    }
  }

  return parts.join('\n');
}
